import { type Area } from 'react-easy-crop'
import { type PostUploadAvatarMutationResult } from '@/types/origin-point-engine'

const MAX_AVATAR_BYTES = 2 * 1024 * 1024
const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
]
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif']

export const AVATAR_ACCEPT =
  '.jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif'

export type AvatarCropResult = {
  blob: Blob
  mimeType: 'image/jpeg'
}

function getFileExtension(name: string) {
  const ext = name.split('.').pop()
  return ext ? ext.toLowerCase() : ''
}

export function validateAvatarFile(file: File) {
  if (file.size > MAX_AVATAR_BYTES) {
    return { valid: false, message: '图片大小不能超过 2MB' }
  }

  if (
    ACCEPTED_MIME_TYPES.includes(file.type) ||
    ACCEPTED_EXTENSIONS.includes(getFileExtension(file.name))
  ) {
    return { valid: true }
  }

  return { valid: false, message: '仅支持 jpg / jpeg / png / gif 格式' }
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.crossOrigin = 'anonymous'
    img.src = url
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: 'image/jpeg',
  quality?: number
) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality)
  })
}

export async function cropToCircularAvatarBlob(args: {
  imageUrl: string
  croppedAreaPixels: Area
  jpegQuality?: number
}): Promise<AvatarCropResult> {
  const { imageUrl, croppedAreaPixels, jpegQuality = 0.92 } = args
  const image = await createImage(imageUrl)

  const cropX = Math.max(0, Math.floor(croppedAreaPixels.x))
  const cropY = Math.max(0, Math.floor(croppedAreaPixels.y))
  const cropWidth = Math.max(1, Math.floor(croppedAreaPixels.width))
  const cropHeight = Math.max(1, Math.floor(croppedAreaPixels.height))
  const size = Math.min(cropWidth, cropHeight)
  const offsetX = cropX + Math.floor((cropWidth - size) / 2)
  const offsetY = cropY + Math.floor((cropHeight - size) / 2)

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas context not available')

  canvas.width = size
  canvas.height = size

  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, size, size)
  ctx.save()
  ctx.beginPath()
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
  ctx.closePath()
  ctx.clip()
  ctx.drawImage(image, offsetX, offsetY, size, size, 0, 0, size, size)
  ctx.restore()

  const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', jpegQuality)
  if (!jpegBlob) {
    throw new Error('导出图片失败')
  }

  const normalizedBlob = jpegBlob.type
    ? jpegBlob
    : new Blob([jpegBlob], { type: 'image/jpeg' })

  return { blob: normalizedBlob, mimeType: 'image/jpeg' }
}

export function extractAvatarUrl(response: PostUploadAvatarMutationResult) {
  const res = response as Record<string, unknown> | null | undefined

  // 安全读取对象上某个键的值（仅当对象为普通对象时）
  const readProp = (obj: unknown, key: string): unknown =>
    obj && typeof obj === 'object'
      ? (obj as Record<string, unknown>)[key]
      : undefined

  const data = readProp(res, 'data')
  const url =
    readProp(readProp(data, 'avatar'), 'url') ??
    readProp(readProp(data, 'image'), 'url') ??
    readProp(data, 'url') ??
    readProp(data, 'imageUrl') ??
    readProp(data, 'fileUrl') ??
    readProp(data, 'path') ??
    readProp(res, 'url') ??
    readProp(res, 'path')

  return typeof url === 'string' ? url.trim() : ''
}
